import { StoreExt } from '@utils/reactExt'
import { action, observable, runInAction } from 'mobx'
import { Schema$CheerForTeam, Schema$SportTalkListItem } from '@interface/cheer-for-team'
import _ from 'lodash'
import { COMMUNICATION_BLOCK_TYPE } from '@v2/constants/common'
import StorageUtils from '@utils/storage-utils'
import { SPORT_TALK_ITEM_TYPE } from '@v2/constants/sport-issue'
import { matchupStore } from '@store/index'

export class CheerForTeamStore extends StoreExt {
  @observable
  sportTalkList: Schema$SportTalkListItem[]

  hasMoreCheerForTeam: boolean

  fetchCheerForTeamPageNum: number

  @observable
  myCheerForTeams: Schema$CheerForTeam[]

  hasMoreMyCheerForTeams: boolean

  fetchMyCheerForTeamPageNum: number

  isFetchCheerForTeamLoading: boolean

  @observable
  effectEnabled: boolean = StorageUtils.getCheerForTeamEffectEnabled()

  @action
  resetFetchCheerForTeamPagination() {
    this.hasMoreCheerForTeam = false
    this.fetchCheerForTeamPageNum = 0
  }

  @action
  resetMyFetchCheerForTeam() {
    this.myCheerForTeams = []
    this.hasMoreMyCheerForTeams = false
    this.fetchMyCheerForTeamPageNum = 0
  }

  async fetchCheerForTeam(matchId: number, reset = false, timelineDisabled = false): Promise<void> {
    try {
      let lastCheerForTeamTime

      if (reset) {
        this.resetFetchCheerForTeamPagination()
      } else {
        lastCheerForTeamTime = _.chain(
          this.sportTalkList
        ).filter({
          sportTalkItemType: SPORT_TALK_ITEM_TYPE.CHEER_FOR_TEAM
        }).last().value()?.createdAt
      }

      const result = await this.api.cheerForTeam.fetchSportTalkList({
        matchId,
        pageNum: this.fetchCheerForTeamPageNum,
        lastCheerForTeamTime,
        timelineDisabled,
      })
      runInAction(() => {
        if (reset) {
          this.sportTalkList = result.items
        } else {
          this.sportTalkList = this.sportTalkList.concat(result.items)
        }
        this.hasMoreCheerForTeam = result.hasMore
      })

      if (result.hasMore) {
        this.fetchCheerForTeamPageNum += 1
      }
    } catch (err) {
      console.error(err)
    }
  }

  @action
  setAttributesByCheerForTeamId(params: { cheerForTeamId: number, reported?: boolean, }): void {
    const { cheerForTeamId, reported } = params
    const origin = _.find(this.sportTalkList, sportTalk => sportTalk.cheerForTeam?.id === cheerForTeamId)
    if (origin && !_.isNil(reported)) {
      origin.cheerForTeam.reported = reported
    }
  }

  @action
  setCheerForTeamsUserBlock(userPromiseId: string, blockTypes: COMMUNICATION_BLOCK_TYPE[]): void {
    const isUserBlock = !_.isEmpty(blockTypes)
    const isCheerForTeamBlock = _.includes(blockTypes, COMMUNICATION_BLOCK_TYPE.CHAT_AND_SPORT_TALK)
    const sportTalks = _.filter(this.sportTalkList, { sportTalkItemType: SPORT_TALK_ITEM_TYPE.CHEER_FOR_TEAM })

    _.forEach(sportTalks, sportTalk => {
      const { cheerForTeam } = sportTalk
      if (cheerForTeam.owner.userPromiseId === userPromiseId) {
        cheerForTeam.isBlocked = isUserBlock
        cheerForTeam.owner.isBlocked = isCheerForTeamBlock
      }
    })

    const matchIds = _.map(sportTalks, talk => talk.cheerForTeam?.matchId)
    matchupStore.setSportTalkUserBlock(matchIds, userPromiseId, isCheerForTeamBlock)
  }

  async deleteCheerForTeam(cheerForTeamId: number) {
    try {
      await this.api.cheerForTeam.deleteCheerForTeam({ cheerForTeamId })
    } catch (err) {
      console.error(err)
    }
  }

  async fetchMyCheerForTeams(baseMonth?: string, reset = false): Promise<void> {
    this.isFetchCheerForTeamLoading = true
    try {
      if (reset) {
        this.resetMyFetchCheerForTeam()
      }

      const params = {
        pageNum: this.fetchMyCheerForTeamPageNum,
      }

      if (!_.isNil(baseMonth)) {
        params['baseMonth'] = baseMonth
      }

      const result = await this.api.my.fetchMyCheerForTeamList(params)

      runInAction(() => {
        this.myCheerForTeams = this.myCheerForTeams.concat(result.items)
        this.hasMoreMyCheerForTeams = result.hasMore
      })

      if (result.hasMore) {
        this.fetchMyCheerForTeamPageNum += 1
      }
    } catch (err) {
      console.error(err)
    }
    this.isFetchCheerForTeamLoading = false
  }

  @action
  toggleEffectEnabled(): void {
    StorageUtils.saveCheerForTeamEffectEnabled(!this.effectEnabled)
    this.effectEnabled = !this.effectEnabled
  }
}

export default new CheerForTeamStore()
